<script setup lang="ts">
  import { ref, computed, onMounted } from 'vue'
  import { useRouter } from 'vue-router'
  import { storeToRefs } from 'pinia'
  import SettingsPage from '@/components/app/settings/SettingsPage.vue'
  import {
    EmailTemplatesService,
    type CustomerEmailTemplate,
    PlaceholderKeyEnum,
    type Placeholder,
    type CustomerUser as User
  } from '@/types/generated'
  import { TableCell, TableRow } from '@/components/ui/table'
  import { Button } from '@/components/ui/button'
  import TableOptionsButton from '@/components/admin/table/TableOptionsButton.vue'
  import { ROUTES } from '@/router/routes'
  import { useTranslation } from '@/composables/useTranslation'
  import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle
  } from '@/components/ui/dialog'
  import { genericErrorToast } from '@/lib/toasts'
  import { usePlaceholderStore } from '@/shared/stores/placeholderStore'
  import { useUserStore } from '@/shared/stores/userStore'
  import { useLawFieldStore } from '@/store/lawFieldStore'
  import { useLawFieldTranslation } from '@/composables/useLawFieldTranslation'
  import PaginatedTableView, {
    type FetchDataOptions,
    type PaginatedResponse,
    type Filter
  } from '@/components/admin/PaginatedTable.vue'

  const router = useRouter()
  const templates = ref<CustomerEmailTemplate[]>([])
  const searchQuery = ref('')
  const selectedType = ref('')
  const selectedLawField = ref('')
  const showDeleteModal = ref(false)
  const templateToDelete = ref<CustomerEmailTemplate | null>(null)
  const { fetchPlaceholders } = usePlaceholderStore()
  const userStore = useUserStore()
  const { fetchUser } = userStore
  const { user } = storeToRefs(userStore)
  const { getLawFieldOptions } = useLawFieldStore()
  const lawFieldOptions = computed(() => getLawFieldOptions(user.value))
  const placeholders = computed<Placeholder[]>(() => usePlaceholderStore().placeholders)
  const { translateLawField } = useLawFieldTranslation()

  const questionnairesAndDocumentsPlaceholder = computed<Placeholder>(() => {
    return placeholders.value.find((placeholder) => {
      return placeholder.key === PlaceholderKeyEnum.QUESTIONNAIRES_AND_DOCUMENTS
    })
  })
  const clientFormalGreetingPlaceholder = computed<Placeholder>(() => {
    return placeholders.value.find((placeholder) => {
      return placeholder.key === PlaceholderKeyEnum.CLIENT_FORMAL_GREETING
    })
  })
  const fullSignaturePlaceholder = computed<Placeholder>(() => {
    return placeholders.value.find((placeholder) => {
      return placeholder.key === PlaceholderKeyEnum.FULL_SIGNATURE
    })
  })

  onMounted(async () => {
    const fetching = [fetchTemplates()]
    if (placeholders.value.length === 0) {
      fetching.push(fetchPlaceholders())
    }
    if (!user.value) {
      fetching.push(fetchUser())
    }
    await Promise.all(fetching)
  })

  const fetchTemplates = async (
    options?: FetchDataOptions
  ): Promise<PaginatedResponse<unknown>> => {
    const filters = options?.filters || []
    const typeFilter = filters.find((filter) => filter.key === 'type')?.value
    const lawFieldFilter = filters.find((filter) => filter.key === 'law_field')?.value
    const response = await EmailTemplatesService.emailTemplatesList({
      limit: options?.limit,
      offset: options?.offset,
      search: options?.search,
      type: typeFilter,
      lawField: lawFieldFilter
    })
    return {
      results: response.results as unknown[],
      next: response.next ?? null,
      previous: response.previous ?? null,
      count: response.count
    }
  }

  const filteredTemplates = computed(() => {
    if (searchQuery.value === '' && selectedType.value === '' && selectedLawField.value === '') {
      return templates.value
    }
    return templates.value.filter(
      (template) =>
        template.name.toLowerCase().includes(searchQuery.value.toLowerCase()) &&
        (selectedType.value === '' || template.type === selectedType.value) &&
        (selectedLawField.value === '' || template.law_field === selectedLawField.value)
    )
  })

  const typeOptions = [
    { value: 'create', title: 'Aufnahme' },
    { value: 'reject', title: 'Ablehnung' }
  ]

  const filterableFields = computed<Filter[]>(() => [
    {
      key: 'type',
      label: 'Type',
      type: 'select',
      options: typeOptions.map((option) => ({ value: option.value, label: option.title }))
    },
    {
      key: 'law_field',
      label: 'Rechtsgebiet',
      type: 'multi-select',
      options:
        lawFieldOptions?.value?.map((option) => ({ value: option.value, label: option.title })) ||
        []
    }
  ])

  const getTypeColor = (type: string) => {
    return type === 'create' ? 'text-green-600' : 'text-red-600'
  }

  const { t } = useTranslation({
    en: {
      create: 'Create',
      reject: 'Reject'
    },
    de: {
      create: 'Aufnahme',
      reject: 'Ablehnung'
    }
  })

  const openDeleteModal = (template: CustomerEmailTemplate) => {
    templateToDelete.value = template
    showDeleteModal.value = true
  }

  const closeDeleteModal = () => {
    showDeleteModal.value = false
    templateToDelete.value = null
  }

  const confirmDelete = async () => {
    if (templateToDelete.value) {
      try {
        await EmailTemplatesService.emailTemplatesDestroy({
          uuid: templateToDelete.value.uuid
        })
        closeDeleteModal()
        await fetchTemplates()
      } catch (error) {
        genericErrorToast()
      }
    }
  }

  const createTemplate = async () => {
    try {
      const newTemplate = await EmailTemplatesService.emailTemplatesCreate({
        requestBody: {
          name: 'Neue Vorlage',
          type: 'create',
          subject: 'Mandatsaufnahme',
          law_field: '',
          html: `<p>${clientFormalGreetingPlaceholder.value.html},</p><p>vielen Dank für Ihre Anfrage an unsere Kanzlei. Wir haben Ihre Anfrage geprüft und würden nun mit der Aufnahme Ihres Mandats beginnen.</p><p>Dazu benötigen wir weitere Informationen von Ihnen, die Sie uns bequem über unser gesichertes System zur Kommunikation mit unserer Kanzlei zukommen lassen können.</p><p>${questionnairesAndDocumentsPlaceholder.value.html}</p><p>Sobald Sie alle Dokumente bzw. Fragebögen vollständig ausgefüllt haben, fahren wir mit Ihrer Anfrage fort.</p><p>${fullSignaturePlaceholder.value.html}</p>`,
          questionnaire_uuids: [],
          form_uuids: [],
          default: false
        }
      })
      await router.push(
        ROUTES.APP.SETTINGS.EMAILS.TEMPLATE_DETAIL.replace(':uuid', newTemplate.uuid)
      )
    } catch (error) {
      console.error('Failed to create new template:', error)
      genericErrorToast()
    }
  }
</script>

<template>
  <SettingsPage v-if="templates && user && placeholders">
    <template #actions>
      <button
        class="inline-flex items-center gap-x-2 rounded-lg border border-transparent bg-purple-600 px-3 py-2
          text-sm font-semibold text-white hover:bg-purple-700 focus:outline-none focus:ring-2
          focus:ring-blue-500 disabled:pointer-events-none disabled:opacity-50"
        @click="createTemplate"
      >
        <svg
          class="mr-2 h-4 w-4"
          fill="none"
          height="24"
          stroke="currentColor"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          viewBox="0 0 24 24"
          width="24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <line
            x1="12"
            x2="12"
            y1="5"
            y2="19"
          />
          <line
            x1="5"
            x2="19"
            y1="12"
            y2="12"
          />
        </svg>
        Vorlage erstellen
      </button>
    </template>
    <div class="space-y-4">
      <PaginatedTableView
        searchable-fields="name"
        :fetch-data="fetchTemplates"
        :items="filteredTemplates"
        :filterable-fields="filterableFields"
      >
        <template #table-head>
          <TableCell>Name</TableCell>
          <TableCell>Typ</TableCell>
          <TableCell>Betreff</TableCell>
          <TableCell>Rechtsgebiet</TableCell>
          <TableCell>Aktualisiert</TableCell>
          <TableCell></TableCell>
        </template>
        <template #table-row="{ items }">
          <TableRow
            v-for="template in items"
            :key="template.uuid"
          >
            <TableCell class="py-1">{{ template.name }}</TableCell>
            <TableCell class="py-1">
              <span :class="getTypeColor(template.type)">
                {{ t(`${template.type}`) }}
              </span>
            </TableCell>
            <TableCell class="py-1">{{ template.subject }}</TableCell>
            <TableCell class="py-1">{{ translateLawField(template.law_field) || '-' }}</TableCell>
            <TableCell class="py-1">
              {{ new Date(template.updated_at).toLocaleDateString('de-DE') }}
            </TableCell>
            <TableCell class="py-1">
              <TableOptionsButton
                :options="[
                  {
                    label: 'Bearbeiten',
                    to: ROUTES.APP.SETTINGS.EMAILS.TEMPLATE_DETAIL.replace(':uuid', template.uuid)
                  },
                  {
                    label: 'Löschen',
                    onClick: () => openDeleteModal(template)
                  }
                ]"
              />
            </TableCell>
          </TableRow>
        </template>
      </PaginatedTableView>
    </div>
  </SettingsPage>

  <!-- Delete Confirmation Modal -->
  <Dialog
    :open="showDeleteModal"
    @update:open="closeDeleteModal"
  >
    <DialogContent>
      <DialogHeader>
        <DialogTitle>Löschen bestätigen</DialogTitle>
        <DialogDescription>
          Sind Sie sicher, dass Sie die E-Mail-Vorlage "{{ templateToDelete?.name }}" löschen
          möchten? Diese Aktion kann nicht rückgängig gemacht werden.
        </DialogDescription>
      </DialogHeader>
      <DialogFooter>
        <Button
          variant="secondary"
          @click="closeDeleteModal"
        >
          Abbrechen
        </Button>
        <Button
          variant="destructive"
          @click="confirmDelete"
        >
          Löschen
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>

<style scoped></style>
